/**
 * Modal to display the published GPS contract (D-059) and record acceptance.
 * Used at purchase time (one contract per purchase): shows the version, the pre-acceptance summary,
 * the full contract with Annex A filled for this purchase and the email that will receive the copy,
 * all before the Accept button.
 */
import React from 'react';
import { ScrollView, StyleSheet, Platform, Dimensions, View } from 'react-native';
import { Modal, Portal, Text, Button, useTheme, Divider } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

export interface GPSContractModalProps {
  visible: boolean;
  onAccept: () => void;
  onReject: () => void;
  accepting?: boolean;
  contractSummary?: string;
  contractText?: string;
  versionId?: string;
  versionDate?: string;
  ownerEmail?: string;
  deviceLabel?: string;
}

/** Plain rendering of the published text: `#` headings and `| label | value` Annex A rows. */
function ContractBody({ text }: { text: string }) {
  return (
    <View>
      {text.split('\n').map((line, index) => {
        if (!line.trim()) return null;
        if (line.startsWith('| ')) {
          const [label, ...rest] = line.slice(2).split(' | ');
          return (
            <Text key={index} variant="bodySmall" style={styles.row}>
              <Text style={styles.bold}>{label.trim()}: </Text>
              {rest.join(' | ').trim()}
            </Text>
          );
        }
        const heading = line.match(/^(#{1,3}) (.*)$/);
        if (heading) {
          return (
            <Text key={index} variant={heading[1].length === 1 ? 'titleMedium' : 'titleSmall'} style={styles.heading}>
              {heading[2]}
            </Text>
          );
        }
        return (
          <Text key={index} variant="bodySmall" style={styles.paragraph}>
            {line}
          </Text>
        );
      })}
    </View>
  );
}

export default function GPSContractModal({
  visible,
  onAccept,
  onReject,
  accepting = false,
  contractSummary,
  contractText,
  versionId,
  versionDate,
  ownerEmail,
  deviceLabel,
}: GPSContractModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const summary = contractSummary ?? t('gps.contractSummary', 'By accepting, you agree to the GPS tracking usage terms (data collection, responsibilities and privacy) as set out in the legal documentation.');

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onReject}
        contentContainerStyle={[styles.modal, { backgroundColor: theme.colors.surface }]}
      >
        <Text variant="headlineSmall" style={styles.title}>
          {t('gps.contractTitle', 'GPS Usage Contract')}
        </Text>
        {versionId && (
          <Text variant="bodyMedium" testID="gps-contract-version" style={styles.meta}>
            {t('gps.contractVersion', 'Version {{version}}, of {{date}}', { version: versionId, date: versionDate ?? '' })}
          </Text>
        )}
        {deviceLabel && (
          <Text variant="bodyMedium" style={styles.deviceLabel}>
            {deviceLabel}
          </Text>
        )}
        <ScrollView style={styles.scroll} nestedScrollEnabled testID="gps-contract-scroll">
          <Text variant="titleSmall" style={styles.heading}>
            {t('gps.contractSummaryHeading', 'Pre-acceptance summary')}
          </Text>
          <Text variant="bodyMedium" style={styles.summary}>
            {summary}
          </Text>
          {contractText ? (
            <>
              <Divider style={styles.divider} />
              <Text variant="titleSmall" style={styles.heading}>
                {t('gps.contractFullHeading', 'Full contract and Annex A')}
              </Text>
              <ContractBody text={contractText} />
            </>
          ) : null}
        </ScrollView>
        {ownerEmail ? (
          <Text variant="bodySmall" testID="gps-contract-recipient" style={styles.meta}>
            {t('gps.contractCopyRecipient', 'A PDF copy of the contract will be sent to {{email}} after Stripe confirms the payment.', { email: ownerEmail })}
          </Text>
        ) : null}
        <Button
          mode="contained"
          onPress={onAccept}
          disabled={accepting}
          loading={accepting}
          style={styles.acceptButton}
          testID="gps-contract-accept-button"
        >
          {accepting ? t('gps.contractAccepting', 'Accepting...') : t('gps.contractAccept', 'Accept')}
        </Button>
        <Button
          mode="outlined"
          onPress={onReject}
          disabled={accepting}
          style={styles.rejectButton}
          testID="gps-contract-reject-button"
        >
          {t('gps.contractReject', 'Cancel')}
        </Button>
      </Modal>
    </Portal>
  );
}

const SCREEN_WIDTH = Dimensions.get('window').width;

const styles = StyleSheet.create({
  modal: {
    padding: 24,
    margin: 20,
    borderRadius: 8,
    maxHeight: '85%',
    ...(Platform.OS === 'web' ? {
      maxWidth: Math.min(720, SCREEN_WIDTH * 0.9),
      width: '90%',
      alignSelf: 'center' as const,
    } : {}),
  },
  title: {
    marginBottom: 4,
  },
  meta: {
    marginBottom: 8,
    opacity: 0.8,
  },
  deviceLabel: {
    marginBottom: 12,
    opacity: 0.8,
  },
  scroll: {
    maxHeight: 420,
    marginBottom: 12,
  },
  summary: {
    lineHeight: 22,
  },
  heading: {
    marginTop: 10,
    marginBottom: 4,
  },
  paragraph: {
    lineHeight: 18,
    marginBottom: 6,
  },
  row: {
    lineHeight: 18,
    marginBottom: 4,
  },
  bold: {
    fontWeight: '700',
  },
  divider: {
    marginVertical: 12,
  },
  acceptButton: {
    marginBottom: 8,
  },
  rejectButton: {},
});
